import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { FormControl, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";

import NewProject from "examples/modal/NewProject/NewProject";
import FTextField from "components/Form/FTextField";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import Constants, {
  ButtonTitles,
  Colors,
  Common,
  ModalContent,
  DropdownOptions,
} from "utils/Constants";
import Validators from "utils/Validations";
import { locationListByIdThunk } from "redux/Thunks/FieldsData";
import { createSnag, updateSnag } from "redux/Thunks/Snag";
import { openSnackbar } from "redux/Slice/Notification";

const SNAG_DETAILS_MAX_LENGTH = 1000;

const STATUS_BUTTON_COLORS = {
  open: { bgColor: Colors.LIGHT_RED, color: Colors.DARK_RED },
  in_discussion: { bgColor: Colors.LIGHT_YELLOW, color: Colors.DARK_YELLOW },
  closed: { bgColor: Colors.LIGHT_GREEN, color: Colors.DARK_GREEN },
};

// Searchable dropdown look shared by the project and location fields
const DROPDOWN_VALUE_STYLE = {
  backgroundColor: Colors.WHITE,
  height: pxToRem(45),
  padding: pxToRem(1),
  menuWidth: 400,
};
const DROPDOWN_LABEL_STYLE = { fontWeight: 500 };
// CustomAutoComplete renders its label inline with no gap - match the other form fields
const DROPDOWN_FIELD_SX = {
  "& > .MuiTypography-root": { display: "block", lineHeight: "20px", mb: 1 },
};
const getOptionTitle = (option) => option?.title || "";

const getInitialForm = () => ({
  project: "",
  location: "",
  details: "",
  status: "open",
});

const getEntityId = (value) =>
  typeof value === "object" && value !== null ? value[Constants.MONGOOSE_ID] : value || "";

/**
 * Create / edit snag form.
 *
 * - Edit: pass `snag`.
 * - Create: pass nothing, or `defaultProject` / `defaultLocation` to prefill.
 * - `lockProjectLocation` disables the project and location fields (used from the Project Map,
 *   where the snag is added for the clicked location).
 */
function SnagFormModal({
  open,
  handleClose,
  snag,
  projectList,
  defaultProject,
  defaultLocation,
  lockProjectLocation,
  onSaved,
}) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(getInitialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [locationList, setLocationList] = useState([]);
  const isMounted = useRef(false);

  const isEdit = Boolean(snag?.[Constants.MONGOOSE_ID]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchLocations = useCallback(
    async (projectId) => {
      if (!projectId) return [];
      const res = await dispatch(locationListByIdThunk({ id: projectId }));
      return Array.isArray(res?.payload?.data) ? res.payload.data : [];
    },
    [dispatch]
  );

  // Initialise the form every time the modal opens
  useEffect(() => {
    if (!open) return;

    let nextForm = getInitialForm();
    if (isEdit) {
      nextForm = {
        project: getEntityId(snag?.project),
        location: getEntityId(snag?.location),
        details: snag?.details || "",
        status: snag?.status || "open",
      };
    } else {
      nextForm = {
        ...nextForm,
        project: getEntityId(defaultProject),
        location: getEntityId(defaultLocation),
      };
    }
    setForm(nextForm);
    setErrors({});
    setLoading(false);

    if (lockProjectLocation) {
      setLocationList(defaultLocation ? [defaultLocation] : []);
      return;
    }

    setLocationList([]);
    if (nextForm.project) {
      fetchLocations(nextForm.project).then((locations) => {
        if (isMounted.current) setLocationList(locations);
      });
    }
  }, [open]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleProjectChange = async (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, project: value, location: "" }));
    setErrors((prev) => ({ ...prev, project: "", location: "" }));
    setLocationList([]);
    const locations = await fetchLocations(value);
    if (!isMounted.current) return;
    setLocationList(locations);
  };

  const handleStatusChange = (_event, value) => {
    if (value) {
      setForm((prev) => ({ ...prev, status: value }));
    }
  };

  const validateForm = () => {
    const tempErrors = {};
    const trimmedDetails = form.details?.trim();

    if (!form.project) tempErrors.project = Constants.REQUIRED;
    if (!form.location) tempErrors.location = Constants.REQUIRED;
    const detailsError = Validators.validate("basic2", trimmedDetails);
    if (detailsError) {
      tempErrors.details = detailsError;
    } else if (trimmedDetails.length > SNAG_DETAILS_MAX_LENGTH) {
      tempErrors.details = Constants.SNAG_DETAILS_MAX_LENGTH_ERROR;
    }

    setErrors((prev) => ({ ...prev, ...tempErrors }));
    return Object.keys(tempErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    const payload = {
      project: form.project,
      location: form.location,
      details: form.details?.trim(),
      status: form.status,
    };
    const res = isEdit
      ? await dispatch(updateSnag({ id: snag[Constants.MONGOOSE_ID], body: payload }))
      : await dispatch(createSnag(payload));
    if (!isMounted.current) return;
    setLoading(false);

    if (res?.payload?.status === Common.API_STATUS_200) {
      dispatch(
        openSnackbar({
          message: isEdit ? Constants.SNAG_UPDATE_SUCCESS : Constants.SNAG_CREATE_SUCCESS,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      handleClose();
      onSaved(res.payload.data?.data);
    } else {
      dispatch(
        openSnackbar({
          message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const projectOptions =
    lockProjectLocation && defaultProject ? [defaultProject] : projectList || [];

  return (
    <NewProject
      title={isEdit ? ModalContent.EDIT_SNAG_TITLE : ModalContent.NEW_SNAG_TITLE}
      openNewProject={open}
      handleCloseNewProject={handleClose}
      handleSave={handleSave}
      actionButton={loading ? ButtonTitles.LOADING : ButtonTitles.SUBMIT}
      disabled={loading}
    >
      <MDBox pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={DROPDOWN_FIELD_SX}>
              <CustomAutoComplete
                label="Project*"
                id="project"
                name="project"
                hint="Select"
                getOptionLabel={getOptionTitle}
                menu={projectOptions}
                value={form.project}
                handleChange={handleProjectChange}
                error={Boolean(errors.project)}
                helperText={errors.project}
                disabled={lockProjectLocation}
                valueStyle={DROPDOWN_VALUE_STYLE}
                labelStyle={DROPDOWN_LABEL_STYLE}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={DROPDOWN_FIELD_SX}>
              <CustomAutoComplete
                label="Location*"
                id="location"
                name="location"
                hint="Select"
                getOptionLabel={getOptionTitle}
                menu={locationList}
                value={form.location}
                handleChange={handleFormChange}
                error={Boolean(errors.location)}
                helperText={errors.location}
                disabled={lockProjectLocation || !form.project}
                valueStyle={DROPDOWN_VALUE_STYLE}
                labelStyle={DROPDOWN_LABEL_STYLE}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FTextField
              label="Snag Details*"
              name="details"
              id="details"
              placeholder="Enter snag details"
              type="textarea"
              defaultRows={4}
              error={Boolean(errors.details)}
              helperText={errors.details}
              value={form.details}
              handleChange={handleFormChange}
            />
          </Grid>
          <Grid item xs={12}>
            <MDTypography
              variant="caption"
              sx={{ fontWeight: 500, color: "#344054", display: "block", mb: 1 }}
            >
              Status*
            </MDTypography>
            <ToggleButtonGroup
              value={form.status}
              exclusive
              onChange={handleStatusChange}
              aria-label="Snag status"
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              {DropdownOptions.SNAG_STATUS_OPTIONS.map((option) => {
                const statusValue = option[Constants.MONGOOSE_ID];
                const isSelected = form.status === statusValue;
                const statusColors = STATUS_BUTTON_COLORS[statusValue];
                return (
                  <ToggleButton
                    key={statusValue}
                    value={statusValue}
                    aria-label={option.title}
                    sx={{
                      textTransform: "none",
                      px: 2.5,
                      py: 0.75,
                      fontSize: pxToRem(14),
                      fontWeight: 600,
                      border: `1px solid ${isSelected ? statusColors.color : "#D0D5DD"} !important`,
                      borderRadius: "8px !important",
                      color: `${isSelected ? statusColors.color : "#344054"} !important`,
                      backgroundColor: `${
                        isSelected ? statusColors.bgColor : Colors.WHITE
                      } !important`,
                    }}
                  >
                    {option.title}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </MDBox>
    </NewProject>
  );
}

const entityShape = PropTypes.shape({
  [Constants.MONGOOSE_ID]: PropTypes.string,
  title: PropTypes.string,
});

SnagFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  snag: PropTypes.objectOf(PropTypes.any),
  projectList: PropTypes.arrayOf(PropTypes.object),
  defaultProject: entityShape,
  defaultLocation: entityShape,
  lockProjectLocation: PropTypes.bool,
  onSaved: PropTypes.func,
};

SnagFormModal.defaultProps = {
  snag: null,
  projectList: [],
  defaultProject: null,
  defaultLocation: null,
  lockProjectLocation: false,
  onSaved: () => {},
};

export default SnagFormModal;
